/**
 * @file   src\Pages\SignIn.tsx
 * @brief  SignIn page.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useParams, useNavigate, useEffect, PWAPrompt , Button} from "../utils/thirdpartyComponents";
import CWInput from "src/components/CWInput";
import CWLogo from "../assets/images/CW-logo.svg";
import VerificationModal from "src/components/VerificationModal";
import * as Yup from 'yup';
import { buildSigninSchema } from "../validations/authSchema";
import { getActiveCountryCode } from "src/utils/countryHelper";
import { pageURLs } from 'src/utils/pageUrls';
import InstallApplication from "src/components/InstallApplication";
import { PHONE_MAX_LENGTH } from "src/utils/constants";
import {formatPhoneNumber, formatPhoneNumberforapipass} from "../utils/helper"
import Closeicon from "../assets/images/close-icon-small.svg";
import { useAppDispatch, useAppSelector } from "src/hooks";
import { RootState } from "src/store";
import { validateUser } from "src/store/actions/authAction";
import { IAuthRequest } from "src/interfaces/authInterface";
import { MessageToaster } from "src/utils/toastUtil";
import { resetAuthState } from "src/store/slices/authSlice";
import Appimg from "../assets/images/CW-appicon.svg";
import IosShareIocn from "../assets/images/ios-share-icon.svg";
import IOSAddIocn from "../assets/images/ios-add-icon.svg";
import { getCompanySettings } from 'src/store/actions/settingsAction';
import { getFromLocalStorage } from 'src/utils/helper';
import FullLoader from '../components/FullLoader';
const SignIn = () => {
      // Route information variable
    const params = useParams();
    const navigate = useNavigate();
      // Declare action dispatch.
    const dispatch = useAppDispatch();
    localStorage.removeItem('iosPwaPrompt')
    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    // Initialize states for formdata
    const [formData, setFormData] = useState({
        custphno: '',
    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        custphno: '',
    });

    localStorage.setItem('selectedBay','') 
    localStorage.removeItem('selectedBay')  // Reset selected bay

    // State to show modal
    const [showVerificationModal, setShowVerificationModal] = useState<boolean>(false);
    const CarwashDataId = localStorage.getItem('carWashId')? parseInt(localStorage.getItem('carWashId')):0
    const [carWashId, setcarWashId] = useState(CarwashDataId);
    const [isCustomLogo, setCustomLogo] = useState(false);
    // Access auth redux state variables.
    const { isSuccess, userInfo, errorCode, errorMessage} = useAppSelector((state: RootState) => state.auth);
    // Handle verification modal
    const handleCloseVerificationModal = () => { 
        setShowVerificationModal(false); 
    }
    
    // Triggers whenever input is entered
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        // Extract only digits from the input value
        const phone_value = e.target.value;
        if (!/^\d*$/.test(phone_value)) {
            e.target.value = phone_value.replace(/\D/g, '');
        }

        const { name, value } = e.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });

        // Change user enters Phone number in usa format
        if (name === 'custphno') {
          const formattedValue = formatPhoneNumber(value);
          setFormData({
            ...formData,
            [name]: formattedValue,
          });
        }
    };

    // Triggers when sign in is done
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();


        try {
            // Validate form data
            await buildSigninSchema(getActiveCountryCode()).validate(formData, { abortEarly: false });
            let carwashIdForSignin = localStorage.getItem('carWashId')? parseInt(localStorage.getItem('carWashId')) : 0
            
            if(isNaN(carwashIdForSignin) || carwashIdForSignin === 0) {
                const request = { carwashname : params.carwashname }
                await fetchAppSettings(request);
                carwashIdForSignin = Number(localStorage.getItem('carWashId'));
            }
            setcarWashId(carwashIdForSignin) 

            const authRequest:IAuthRequest = {carWashId : carwashIdForSignin,
                phoneNumber : formatPhoneNumberforapipass(formData.custphno),
                CarWashURL:params.carwashname
            }
            dispatch(validateUser(authRequest));
            toaster.toastDismiss();
            //Clear errors
            setErrors({
                custphno: '',
            });

        } catch (error) {

            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors:any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }
    }

    
  const showSignUpPage = ()=>{
    navigate(pageURLs.signup);

  }

  //   initial loading
    useEffect(() => {
        dispatch(resetAuthState());
    }, []);

    useEffect(() => {
        if(carWashId === null || !carWashId || carWashId === undefined) {
            const request = { carwashname : params.carwashname }
            fetchAppSettings(request);
        }
        localStorage.removeItem('CARWASH_COMPANY_DATA');
    }, [carWashId]);

  // show otp screen on login success.
  useEffect(() => {
    if (isSuccess && userInfo?.carWashId && localStorage.getItem('CARWASH_COMPANY_DATA')) {
        localStorage.setItem('carWashId',userInfo?.carWashId)
        localStorage.setItem("carwashname",params?.carwashname? params?.carwashname : "")
        setShowVerificationModal(true)
    }
  }, [isSuccess]);

   
   
      // Show toast error message if login failed.
  useEffect(() => {
    if (errorCode && errorMessage) {
        setFormData({
            ...formData,
           custphno: '',
        });
      toaster.toastError(errorMessage);
    }
  }, [errorCode,errorMessage]);


  const settings = getFromLocalStorage("CARWASH_COMPANY_SETTINGS_DATA") || {};
  const IsCustom = settings?.IsCustom ? settings.IsCustom : 0;
  
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  
  useEffect(() => {
      let logoFromStorage = IsCustom === 0 ? "" : getFromLocalStorage("LOGO_URL_SETTINGS_DATA");
  
      if (logoFromStorage && IsCustom !== 0) {
          const logoParts = logoFromStorage.split("/");
        if (logoParts.length > 1) {
            logoFromStorage = logoParts[0]+'/resized/160_'+logoParts[1];
            setCustomLogo(true)
        } else {
            setCustomLogo(false)
        }
        setLogoUrl(logoFromStorage);
      } else {
        setCustomLogo(false)
        setLogoUrl(CWLogo)
      }
      
  }, [settings]); 

  useEffect(() => {
    if (logoUrl) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = (isCustomLogo) ? `${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${logoUrl}` : CWLogo;
        document.head.appendChild(link);
        return () => {
            document.head.removeChild(link); 
        };
    }
}, [logoUrl,isCustomLogo]); // preloading


  const fetchAppSettings = async (request : any) => {
    await dispatch(getCompanySettings(request));
   }

   const onIosPromptClose = () => {
    localStorage.setItem('closedIosPrompt',"true")
  }

    return (
        <div className="cw-container">
            <div className="cw-logo text-center">
            {getFromLocalStorage('carWashId') ? 
            
           logoUrl &&

           <picture>
                <img 
                src={isCustomLogo ? `${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${logoUrl}` : CWLogo} 
                alt="Car Wash Logo" 
                width={160}
                height="auto"
                loading="eager" 
                decoding="async"
                fetchPriority="high"
                />
            </picture>
                    : ''}  
                </div>
            <div className="signin-block">
                <h1>Sign In</h1>
                <p className="sub-text">Enter your sign in information</p>
                <form onSubmit={(e) => { handleSubmit(e) }}>
                    <CWInput
                        id="custphno"
                        name="custphno"
                        type="text"
                        inputmode="numeric" // This helps mobile devices to show a numeric keypad
                        label="Phone Number"
                        autoFocus={true}
                        maxLength={PHONE_MAX_LENGTH}
                        value={formData.custphno}
                        errorMessage={errors.custphno && errors.custphno}
                        onChange={handleInputChange}
                    />
                    <Button
                        type="submit"
                        variant="primary"
                        className="primary-btn btn-main"
                    >
                        Sign In
                    </Button>
                </form>
                {getFromLocalStorage('carWashId') ? <div className="signin-link text-center">
                    <p>Don't have an account?</p>
                    <Button
                        type="submit"
                        variant="primary"
                        className="secondary-btn btn-main"
                        onClick={() => showSignUpPage()}
                        title="Sign Up"
                    >
                        Sign Up
                    </Button>
                    {/* <Link to={pageURLs.signup} title="Sign Up" >Sign Up</Link> */}
                </div> : ''}
                
            </div>
            {getFromLocalStorage('carWashId') ? <VerificationModal
                show={showVerificationModal}
                onModalHide={handleCloseVerificationModal}
                verificationFrom="signIn"
                phoneNumber = {formData.custphno}
            /> : ''}
            {(localStorage.getItem('installPromptCancelled') !== 'true' && getFromLocalStorage('carWashId'))? <InstallApplication /> : ''}
            {/* PWA installation prompt for ios */}

            {getFromLocalStorage('carWashId') ? <PWAPrompt 	 delay={1000} permanentlyHideOnDismiss={false}  promptOnVisit={1} timesToShow={100} copyClosePrompt={<img src={Closeicon} width={14} height={14} alt="Close" />} appIconPath={logoUrl ? `${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${logoUrl}` : Appimg } copyShareStep = {<div dangerouslySetInnerHTML={{__html: 'Press the "Share" <img src="'+IosShareIocn+'" alt="Car Wash App" width="25px" height="25px">  button on menu bar below, then press "Add to home screen" <img src="'+IOSAddIocn+'" alt="Car Wash App" width="18px" height="18px"> button'}} />} copyAddToHomescreenStep = {""} onClose={onIosPromptClose}/>: ''}
          {!getFromLocalStorage('carWashId') ? <FullLoader/> : ''}

        </div>
    )
}

export default SignIn;
